import type { CompanyBusinessMode } from '~/swagger/portal/Api';

/**
 * Pure decision logic for keeping the active account aligned with the portal host,
 * split out from the `auth` singleton so it's testable without a browser or the
 * auth library. The two portals (HOA / Property Management) are separate origins,
 * but a cross-host login handoff can leave an origin signed in with the SIBLING
 * portal's account still active — this decides how to repair that on load.
 *
 * Business mode is only KNOWN for companies loaded before in this browser (the JWT
 * doesn't carry it), so `activeMode` and `modeOf(...)` can be `undefined`.
 */

export type HostCorrectionInput = {
  /** The account active on this origin, or null if none. */
  activeAccountId: string | null;
  /** The business mode THIS host's portal serves. */
  thisHostMode: CompanyBusinessMode;
  /** All accounts signed in on this origin. */
  accountIds: string[];
  /** Known business mode for an account, or undefined if never learned. */
  modeOf: (accountId: string) => CompanyBusinessMode | undefined;
  /** The account last made active on this host's mode, if remembered. */
  lastAccountForThisHost: string | undefined;
};

export type HostCorrectionDecision = { action: 'unchanged' } | { action: 'switch'; targetAccountId: string } | { action: 'needs-signin' };

/**
 * Decide how to align the active account with this host:
 *  - active account's mode matches this host, or is unknown -> 'unchanged'. We
 *    never force-switch away from an account we can't prove belongs elsewhere.
 *  - active account's mode is the OTHER host's -> 'switch' to the last account used
 *    on this host (falling back to the first signed-in account whose known mode
 *    matches this host).
 *  - active account is wrong-host but NO signed-in account matches this host ->
 *    'needs-signin'.
 */
export const decideHostCorrection = (input: HostCorrectionInput): HostCorrectionDecision => {
  const { activeAccountId, thisHostMode, accountIds, modeOf, lastAccountForThisHost } = input;
  if (!activeAccountId) return { action: 'unchanged' };

  const activeMode = modeOf(activeAccountId);
  if (activeMode === undefined || activeMode === thisHostMode) return { action: 'unchanged' };

  const matching = accountIds.filter((id) => modeOf(id) === thisHostMode);
  if (!matching.length) return { action: 'needs-signin' };

  const target = lastAccountForThisHost && matching.includes(lastAccountForThisHost) ? lastAccountForThisHost : matching[0];
  return { action: 'switch', targetAccountId: target };
};

/**
 * Before handing the active account off to the sibling portal host, pick a
 * same-host account to keep active on THIS origin instead — so this origin isn't
 * left with the other portal's account active. Returns undefined when there's no
 * same-host account to fall back to (leave the active account as-is; the on-load
 * {@link decideHostCorrection} will send the user to sign-in on the next visit).
 */
export const pickHandoffFallbackAccount = (input: {
  activeAccountId: string | null;
  thisHostMode: CompanyBusinessMode;
  accountIds: string[];
  modeOf: (accountId: string) => CompanyBusinessMode | undefined;
}): string | undefined => {
  const { activeAccountId, thisHostMode, accountIds, modeOf } = input;
  if (!activeAccountId) return undefined;
  return accountIds.find((id) => id !== activeAccountId && modeOf(id) === thisHostMode);
};
